import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { createProviderUserThunk, resetProviderUserState } from '@/features/user/providerUserSlice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const CreateProviderUserForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const { loading, error, success, createdUser } = useSelector((state: RootState) => state.providerUser);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    title: '',
    location: '',
    mobile: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await dispatch(createProviderUserThunk(formData)).unwrap();
      toast({
        title: 'Success',
        description: 'Provider user created successfully',
        variant: 'default',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: typeof err === 'string' ? err : 'Failed to create provider user',
        variant: 'destructive',
      });
    }
  };

  const handleReset = () => {
    setFormData({
      username: '',
      email: '',
      title: '',
      location: '',
      mobile: ''
    });
    dispatch(resetProviderUserState());
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Create Provider User</CardTitle>
        <CardDescription>
          Create a new user with provider role. A random password will be generated and returned.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && createdUser && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">User Created Successfully</AlertTitle>
            <AlertDescription className="text-green-700">
              <p>Username: {createdUser.user.username}</p>
              <p>Email: {createdUser.user.email}</p>
              <p>Generated Password: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{createdUser.generatedPassword}</span></p>
              <p className="mt-2 text-sm">Please save this password as it will not be shown again.</p>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="provider_name"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="provider@example.com"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="University Provider"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, Country"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="+1234567890"
              />
            </div>
          </div>
          
          <CardFooter className="flex justify-between px-0 pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !formData.username || !formData.email}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Provider User'
              )}
            </Button>
          </CardFooter>
        </form>
      </CardContent>
    </Card>
  );
};

export default CreateProviderUserForm;
